import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  Chip,
  FormGroup,
  Switch,
  ListItemText,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { errorToast, successToast } from "../../helpers";
import Loader from "../../helpers/Loader";
import { useGetTutorListQuery } from "../../services/class";
import {
  useUpdatePromoMutation,
  useLazyGetPromoByIdQuery,
  useAddPromoMutation,
} from "../../services/promocodes";
import { CommonBody } from "../../types/General";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { useLazyGetCatSubListQuery } from "../../services/category";
import { LISTING } from "../../helpers/constants/enums";
import { isValidInput } from "../../utils/validation";

// Constants
const PROMOCODE_TYPE = {
  ONE_TO_ONE: 1,
  CLASS: 2,
  BOTH: 3,
};

const SERVICE_TYPE = {
  FLAT: 1,
  PERCENT: 2,
};

const AddPromo = () => {
  const navigate = useNavigate();
  const { _id } = useParams();
  const { data: tutorData } = useGetTutorListQuery();
  const [getByID] = useLazyGetPromoByIdQuery();
  const [addPromo] = useAddPromoMutation();
  const [updatePromo] = useUpdatePromoMutation();
  const [selectedTutor, setSelectedTutor] = useState("0");
  const [promoDetails, setPromoDetails] = useState<any>();
  const [classIds, setClassIds] = useState<string[]>([]);
  const [allClasses, setAllClasses] = useState(false);
  const [discountType, setDiscountType] = useState(SERVICE_TYPE.PERCENT);
  const [classIdsData] = useLazyGetCatSubListQuery();
  const [allClass, setAllClass] = useState<any>([]);
  const [schedulePromotion, setSchedulePromotion] = useState(false);

  const allClassGet = async () => {
    if (!selectedTutor) return;

    try {
      const response = await classIdsData({
        type: LISTING.CLASSES,
        tutorId: formik.values.tutor,
      }).unwrap();

      if (response.statusCode === 200) {
        setAllClass(response?.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleTutorChange = (event: any) => {
    setSelectedTutor(event.target.value);
    formik.setFieldValue("tutor", event.target.value);
  };

  const handleSchedulePromotionToggle = () => {
    setSchedulePromotion(!schedulePromotion);
    if (!schedulePromotion) {
      // When enabling scheduling, set start date to today if empty
      if (!formik.values.startDate) {
        formik.setFieldValue(
          "startDate",
          new Date().toISOString().split("T")[0]
        );
      }
    } else {
      // When disabling scheduling, clear the start date
      formik.setFieldValue("startDate", "");
    }
  };

  // const handleDiscountTypeChange = (event: any) => {
  //   setDiscountType(event.target.value);
  // };

  const handleDiscountTypeToggle = () => {
    setDiscountType((prevType) =>
      prevType === SERVICE_TYPE.PERCENT
        ? SERVICE_TYPE.FLAT
        : SERVICE_TYPE.PERCENT
    );
    // Reset discount value when switching types
    formik.setFieldValue("discount", "");
  };

  const handleAllClassesChange = (event: any) => {
    setAllClasses(event.target.checked);
    if (event.target.checked) {
      setClassIds([]);
    }
  };

  const handleClassIdsChange = (event: any) => {
    const value = event.target.value;
    console.log(value, "classIdValues");
    if (value.includes("select-all")) return;
    setClassIds(value);
  };

  const fetchPromo = async () => {
    try {
      const response = await getByID({ id: _id }).unwrap();
      if (response.statusCode === 200) {
        setPromoDetails(response?.data);
        setSelectedTutor(response?.data?.tutor?._id || "0");
        setDiscountType(response?.data?.discountType || SERVICE_TYPE.PERCENT);
        setAllClasses(response?.data?.allClasses || false);
        setClassIds(response?.data?.classIds || []);

        formik.setFieldValue("name", response?.data?.name);
        formik.setFieldValue("discount", response?.data?.discount);
        formik.setFieldValue("noOfCodes", response?.data?.maxUser);
        formik.setFieldValue("description", response?.data?.description || "");
        formik.setFieldValue("codeName", response?.data?.codeName || "-");
        formik.setFieldValue("type", response?.data?.type || "");
        formik.setFieldValue(
          "expiryDate",
          response?.data?.expiryDate
            ? response.data.expiryDate.split("T")[0]
            : new Date().toISOString().split("T")[0]
        );
        formik.setFieldValue("tutor", response?.data?.tutor?._id || "");
        formik.setFieldValue(
          "startDate",
          response?.data?.startDate
            ? response.data.startDate.split("T")[0]
            : new Date().toISOString().split("T")[0]
        );
        setSchedulePromotion(response?.data?.startDate ? true : false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      discount: "",
      noOfCodes: "",
      tutor: "",
      expiryDate: "",
      startDate: "",
      description: "",
      type: PROMOCODE_TYPE.ONE_TO_ONE,
      codeName: "",
    },
    validationSchema: Yup.object()
      .shape({
        name: Yup.string().required("Promocode name is required"),
        discount: Yup.number()
          .min(1, "Discount must be at least 1")
          // .when('discountType', {
          //   is: SERVICE_TYPE.PERCENT,
          //   then: Yup.number().max(100, "Discount cannot exceed 100%"),
          // })
          .required("Discount is required"),
        noOfCodes: Yup.string().required("Must allow at least 1 user"),
        // .required("Number of codes is required")
        tutor: Yup.string().required("Tutor is required"),
        expiryDate: Yup.string()
          // .min(Yup.ref("startDate"), "Expiry date must be after start date")
          .required("Expiry date is required"),
        startDate: Yup.date().min(
          new Date(),
          "Start date must be today or in the future"
        ),
        // .required("Start date is required"),
        // type: Yup.number()
        //   .oneOf([
        //     PROMOCODE_TYPE.CLASS,
        //     PROMOCODE_TYPE.ONE_TO_ONE,
        //     PROMOCODE_TYPE.BOTH,
        //   ])
        //   .required("Type is required"),
        type: Yup.number()
          .oneOf([
            PROMOCODE_TYPE.CLASS,
            PROMOCODE_TYPE.ONE_TO_ONE,
            PROMOCODE_TYPE.BOTH,
          ])
          .required("Type is required"),
      })
      .when("type", {
        is: (type: number) =>
          type === PROMOCODE_TYPE.CLASS || type === PROMOCODE_TYPE.BOTH,
        then: (schema) =>
          schema.shape({
            classIds: Yup.array()
              .min(1, "At least one class must be selected")
              .required("Class selection is required"),
          }),
      }),

    onSubmit: async (values) => {
      const body = {
        tutorId: values.tutor,
        name: values.name,
        ...(values.codeName ? { codeName: values.codeName } : {}),
        discount: values.discount,
        discountType: discountType,
        maxUser: values.noOfCodes,
        classIds: allClasses ? [] : classIds,
        allClasses: allClasses,
        type: values.type,
        // expiryDate: values.expiryDate,
        // ...(schedulePromotion ? { startDate: values.startDate } : {}),
        expiryDate: new Date(values.expiryDate).toISOString(),
        ...(schedulePromotion
          ? { startDate: new Date(values.startDate).toISOString() }
          : {}),
      };
      console.log(body, "body");

      if (_id) {
        try {
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response: any = await updatePromo({
            body: encryptedBody,
            id: _id,
          }).unwrap();
          if (response?.statusCode === 200) {
            successToast("Promocodes updated successfully");
            navigate("/manage-promocodes");
          }
        } catch (error: any) {
          console.error("Error updating promocodes:", error);
          errorToast(error?.data?.message || "");
        }
      } else {
        try {
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response: any = await addPromo({
            body: encryptedBody,
          }).unwrap();
          if (response?.statusCode === 200) {
            successToast("Promocodes added successfully");
            navigate("/manage-promocodes");
          }
        } catch (error: any) {
          console.error("Error adding promocodes:", error);
          errorToast(error?.data?.message || "");
        }
      }
    },
  });

  useEffect(() => {
    if (Number(formik.values.type) === PROMOCODE_TYPE.ONE_TO_ONE) {
      setClassIds([]);
      setAllClasses(false);
    }
  }, [formik.values.type]);

  useEffect(() => {
    if (formik.values.tutor) allClassGet();
  }, [formik.values.tutor]);

  useEffect(() => {
    if (_id) {
      fetchPromo();
    }
  }, [_id]);

  console.log(formik.errors, "formikerror");

  return (
    <div className="main_loyout">
      <div className="dashboard">
        <h1 className="mn_hdng">{_id ? "Edit Promocode" : "Add Promocode"}</h1>
        <Loader isLoad={formik.isSubmitting} />
        <Button
          className="btn btn_primary"
          onClick={() => navigate("/manage-promocodes")}
        >
          Back
        </Button>
      </div>

      <Card className="cards">
        <form onSubmit={formik.handleSubmit}>
          <CardContent sx={{ p: 1 }}>
            <Grid container spacing={2}>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Typography className="custom_label">
                  Promo Code Name
                </Typography>
                <TextField
                  className="text_field"
                  fullWidth
                  name="name"
                  placeholder="Promo Code Name"
                  value={formik.values.name}
                  onChange={(e) => {
                    if (!isValidInput(e.target.value)) {
                      return;
                    }
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Typography className="custom_label">
                  Custom Code Name (optional)
                </Typography>
                <TextField
                  className="text_field"
                  fullWidth
                  name="codeName"
                  placeholder="Leave blank for auto-generation"
                  value={formik.values.codeName}
                  onChange={(e) => {
                    if (!isValidInput(e.target.value)) {
                      return;
                    }
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.codeName && Boolean(formik.errors.codeName)
                  }
                  helperText={formik.touched.codeName && formik.errors.codeName}
                />
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Typography className="custom_label">Max User</Typography>
                <TextField
                  className="text_field"
                  fullWidth
                  type="text"
                  name="noOfCodes"
                  placeholder="Max User"
                  value={formik.values.noOfCodes}
                  onChange={(e) => {
                    let value = parseInt(e.target.value);
                    if (isNaN(value)) value = 1;
                    formik.setFieldValue("noOfCodes", value);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.noOfCodes && Boolean(formik.errors.noOfCodes)
                  }
                  helperText={
                    formik.touched.noOfCodes && formik.errors.noOfCodes
                  }
                />
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Typography className="custom_label">Discount Type</Typography>
                <FormGroup>
                  <Typography component="div">
                    <Grid
                      component="label"
                      container
                      alignItems="center"
                      spacing={1}
                    >
                      <Grid item>Percentage</Grid>
                      <Grid item>
                        <Switch
                          checked={discountType === SERVICE_TYPE.FLAT}
                          onChange={handleDiscountTypeToggle}
                          color="primary"
                        />
                      </Grid>
                      <Grid item>Flat Amount</Grid>
                    </Grid>
                  </Typography>
                </FormGroup>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Typography className="custom_label">
                  {discountType === SERVICE_TYPE.PERCENT
                    ? "Discount (%)"
                    : "Discount Amount"}
                </Typography>
                <TextField
                  className="text_field"
                  fullWidth
                  type="text"
                  name="discount"
                  placeholder={
                    discountType === SERVICE_TYPE.PERCENT
                      ? "Enter percentage (1-100)"
                      : "Enter amount"
                  }
                  value={formik.values.discount}
                  onChange={(e) => {
                    let value = parseFloat(e.target.value);
                    if (isNaN(value)) value = 0;

                    // Apply different validation based on discount type
                    if (discountType === SERVICE_TYPE.PERCENT) {
                      if (value > 100) value = 100;
                      if (value < 1) value = 1;
                    } else {
                      // For flat amount, ensure it's positive
                      if (value < 0) value = 0;
                    }

                    formik.setFieldValue("discount", value);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.discount && Boolean(formik.errors.discount)
                  }
                  helperText={formik.touched.discount && formik.errors.discount}
                  inputProps={{
                    min: discountType === SERVICE_TYPE.PERCENT ? 1 : 0,
                    max:
                      discountType === SERVICE_TYPE.PERCENT ? 100 : undefined,
                    step: discountType === SERVICE_TYPE.PERCENT ? 1 : 0.01,
                  }}
                />
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Typography className="custom_label">Tutor</Typography>
                <FormControl fullWidth>
                  <Select
                    className="select_div"
                    value={selectedTutor}
                    onChange={handleTutorChange}
                    error={formik.touched.tutor && Boolean(formik.errors.tutor)}
                  >
                    <MenuItem value="0" disabled>
                      Select Tutor
                    </MenuItem>
                    {tutorData?.data?.map((item: any) => (
                      <MenuItem key={item._id} value={item._id}>
                        {item.userName} ({item.name})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Typography className="custom_label">Expiry Date</Typography>
                <TextField
                  fullWidth
                  type="date"
                  name="expiryDate"
                  className="text_field"
                  value={formik.values.expiryDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  inputProps={{
                    min:
                      formik.values.startDate ||
                      new Date().toISOString().split("T")[0],
                  }}
                  error={
                    formik.touched.expiryDate &&
                    Boolean(formik.errors.expiryDate)
                  }
                  helperText={
                    formik.touched.expiryDate && formik.errors.expiryDate
                  }
                />
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Typography className="custom_label">
                  Schedule Promotion Start
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={schedulePromotion}
                        onChange={handleSchedulePromotionToggle}
                        color="primary"
                      />
                    }
                    label={
                      schedulePromotion ? "Scheduled" : "Start Immediately"
                    }
                  />
                </FormGroup>
              </Grid>

              {schedulePromotion && (
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <Typography className="custom_label">Start Date</Typography>
                  <TextField
                    fullWidth
                    type="date"
                    name="startDate"
                    className="text_field"
                    value={formik.values.startDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    inputProps={{
                      min: new Date().toISOString().split("T")[0],
                    }}
                    error={
                      formik.touched.startDate &&
                      Boolean(formik.errors.startDate)
                    }
                    helperText={
                      formik.touched.startDate && formik.errors.startDate
                    }
                  />
                </Grid>
              )}

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Typography className="custom_label">Class Type</Typography>
                <FormControl fullWidth>
                  <Select
                    className="select_div"
                    name="type"
                    value={formik.values.type}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.type && Boolean(formik.errors.type)}
                  >
                    <MenuItem value={""} disabled>
                      Select
                    </MenuItem>
                    <MenuItem value={PROMOCODE_TYPE.ONE_TO_ONE}>
                      One-to-One
                    </MenuItem>
                    <MenuItem value={PROMOCODE_TYPE.CLASS}>Class</MenuItem>
                    <MenuItem value={PROMOCODE_TYPE.BOTH}>Both</MenuItem>
                  </Select>
                </FormControl>
                {formik.errors.type && (
                  <Typography color={"red"}>{formik.errors.type}</Typography>
                )}
              </Grid>

              {formik.values.type &&
                Number(formik.values.type) !== PROMOCODE_TYPE.ONE_TO_ONE && (
                  <Grid item lg={4} md={4} sm={6} xs={12}>
                    <Typography className="custom_label">Class IDs</Typography>
                    <FormControl fullWidth>
                      <Select
                        className="select_div"
                        multiple
                        value={classIds}
                        onChange={handleClassIdsChange}
                        renderValue={(selected: any) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {selected.length === allClass.length ? (
                              <Chip key="all" label="All Classes" />
                            ) : (
                              selected.map((value: any) => (
                                <Chip
                                  key={value}
                                  label={
                                    allClass.find((p: any) => p._id === value)
                                      ?.topic || value
                                  }
                                />
                              ))
                            )}
                          </Box>
                        )}
                      >
                        <MenuItem
                          value="select-all"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (classIds.length === allClass.length) {
                              setClassIds([]);
                            } else {
                              setClassIds(
                                allClass.map((item: any) => item._id)
                              );
                            }
                          }}
                        >
                          <Checkbox
                            checked={classIds.length === allClass.length}
                            indeterminate={
                              classIds.length > 0 &&
                              classIds.length < allClass.length
                            }
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {allClass?.map((item: any) => (
                          <MenuItem
                            key={item._id}
                            value={item._id}
                            disabled={classIds.length === allClass.length}
                          >
                            <Checkbox
                              checked={classIds.indexOf(item._id) > -1}
                            />
                            <ListItemText primary={item?.topic} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
            </Grid>

            <div className="form_btn">
              <Button size="large" type="submit" className="btn btn_primary">
                {"Save"}
              </Button>
            </div>
          </CardContent>
        </form>
      </Card>
    </div>
  );
};

export default AddPromo;
